const React = require('react');

// Sections whose chunk has not hydrated yet. The DOM-based currency changer
// must leave their server HTML untouched until React has hydrated it: a text
// mismatch makes React discard the section and re-render it from scratch,
// which shifts everything below it (CLS).
const pending = new Set();
let waiters = [];

function settle() {
    if (pending.size > 0) return;
    const resolved = waiters;
    waiters = [];
    resolved.forEach((resolve) => resolve());
}

function whenSectionsHydrated() {
    return new Promise((resolve) => {
        if (pending.size === 0) resolve();
        else waiters.push(resolve);
    });
}

// Mounts together with the section, so its effect runs once the boundary has
// hydrated (or client-rendered).
function SectionReady({ section }) {
    React.useEffect(() => {
        section.ready = true;
        pending.delete(section);
        settle();
    }, [section]);
    return null;
}

// Gatsby 5 waits for onAllReady when rendering static HTML. This boundary
// preserves that HTML while the browser loads only the sections a page uses.
function lazySection(load) {
    const Component = React.lazy(load);
    return function LazySection(props) {
        const section = React.useRef({ ready: false }).current;
        React.useEffect(() => {
            // Child effects run first, so a section that hydrated in the same
            // commit is already marked ready here.
            if (!section.ready) pending.add(section);
            return () => {
                pending.delete(section);
                settle();
            };
        }, [section]);
        return React.createElement(
            React.Suspense,
            { fallback: null },
            React.createElement(Component, props),
            React.createElement(SectionReady, { section })
        );
    };
}

module.exports = lazySection;
module.exports.whenSectionsHydrated = whenSectionsHydrated;
